import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDnsRecord, DNS_RECORD_TYPE_NAMES } from '../../services/dnsService';
import { isValidIP } from '../../utils/helpers';
import { useDeviceType } from '../../hooks/useDeviceType';

const RecordTable = ({ dnsData, rawDnsData }) => {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const [showRaw, setShowRaw] = useState(false);
  
  if (!dnsData) return null;

  const { isMobile, isTablet } = useDeviceType();

  const containerStyle = {
    background: colors.cardBackground,
    borderRadius: '16px',
    padding: isMobile ? '1.5rem' : '2rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)',
    marginTop: isMobile ? '1.5rem' : '2rem'
  };

  const titleStyle = {
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    fontWeight: '600',
    color: colors.text,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const sectionStyle = {
    marginBottom: isMobile ? '1.5rem' : '2rem'
  };

  const sectionTitleStyle = {
    fontSize: isMobile ? '1rem' : '1.1rem',
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: '1rem',
    padding: '0.5rem 0',
    borderBottom: `2px solid ${colors.border}`
  };

  const tableContainerStyle = {
    overflowX: 'auto',
    maxWidth: '100%',
    // 添加移动端专用的滚动样式
    WebkitOverflowScrolling: 'touch'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '1rem',
    minWidth: isMobile ? '600px' : 'auto' // 确保表格内容不会挤压
  };

  const thStyle = {
    background: colors.primary,
    color: 'white',
    padding: isMobile ? '0.6rem 0.5rem' : '0.75rem',
    textAlign: 'left',
    fontSize: isMobile ? '0.8rem' : '0.9rem',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: isMobile ? '0.6rem 0.5rem' : '0.75rem',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: isMobile ? '0.8rem' : '0.9rem',
    fontFamily: 'Monaco, Consolas, monospace',
    verticalAlign: 'top',
    color: colors.text
  };

  const emptyStyle = {
    textAlign: 'center',
    color: colors.textSecondary,
    fontStyle: 'italic',
    padding: isMobile ? '1.5rem' : '2rem',
    background: colors.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
    borderRadius: '8px',
    fontSize: isMobile ? '0.85rem' : '1rem'
  };

  const recordTypeColor = (type) => {
    const colorMap = {
      'A': '#4CAF50',
      'AAAA': '#2196F3',
      'CNAME': '#FF9800',
      'MX': '#9C27B0',
      'TXT': '#795548',
      'NS': '#607D8B',
      'SOA': '#E91E63'
    };
    return colorMap[type] || colors.textSecondary;
  };

  const formatTTL = (ttl) => {
    // 确保 ttl 是数字
    const ttlNum = parseInt(ttl) || 0;
    
    // 获取当前语言
    const currentLang = i18n.language;
    
    if (ttlNum < 60) {
      return currentLang === 'zh' ? `${ttlNum}秒` : `${ttlNum}s`;
    }
    if (ttlNum < 3600) {
      const minutes = Math.floor(ttlNum / 60);
      return currentLang === 'zh' ? `${minutes}分钟` : `${minutes}m`;
    }
    if (ttlNum < 86400) {
      const hours = Math.floor(ttlNum / 3600);
      return currentLang === 'zh' ? `${hours}小时` : `${hours}h`;
    }
    const days = Math.floor(ttlNum / 86400);
    return currentLang === 'zh' ? `${days}天` : `${days}d`;
  };

  const renderRecordTable = (records, title, emoji) => {
    if (!records || records.length === 0) {
      return (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>{emoji} {title}</h3>
          <div style={emptyStyle}>{t('dns.results.empty', { type: title })}</div>
        </div>
      );
    }

    return (
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>{emoji} {title} ({records.length}条记录)</h3>
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>{t('dns.results.columns.name')}</th>
                <th style={thStyle}>{t('dns.results.columns.type')}</th>
                <th style={thStyle}>{t('dns.results.columns.ttl')}</th>
                <th style={thStyle}>{t('dns.results.columns.value')}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => {
                const formatted = formatDnsRecord(record);
                return (
                  <tr key={index}>
                    <td style={tdStyle}>{formatted.name}</td>
                    <td style={{
                      ...tdStyle,
                      color: recordTypeColor(formatted.type),
                      fontWeight: '600'
                    }}>
                      {formatted.type}
                    </td>
                    <td style={tdStyle}>{formatTTL(formatted.ttl)}</td>
                    <td style={tdStyle}>
                      <div style={{ 
                        wordBreak: 'break-all',
                        maxWidth: isMobile ? '200px' : '300px',
                        lineHeight: '1.3'
                      }}>
                        {isValidIP(formatted.data) ? (
                          <Link to={`/?ip=${formatted.data}`} style={{ color: colors.link, textDecoration: 'none' }}>
                            {formatted.data}
                          </Link>
                        ) : (
                          formatted.data
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isMobile && (
          <div style={{
            fontSize: '0.7rem',
            color: colors.textSecondary,
            textAlign: 'center',
            marginTop: '0.5rem',
            fontStyle: 'italic'
          }}>
            💡 横向滑动查看完整表格内容
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>
        {t('dns.results.title')} 🔍
      </h2>

      {dnsData.question && dnsData.question.length > 0 && (
        <div style={{
          background: colors.isDarkMode 
            ? 'rgba(102, 126, 234, 0.15)' 
            : 'rgba(102, 126, 234, 0.08)',
          border: `1px solid ${colors.isDarkMode ? 'rgba(102, 126, 234, 0.3)' : colors.border}`,
          padding: isMobile ? '0.8rem' : '1rem',
          borderRadius: '8px',
          marginBottom: isMobile ? '1.5rem' : '2rem'
        }}>
          <strong style={{ 
            color: colors.isDarkMode ? colors.link : '#1565c0',
            fontSize: isMobile ? '0.85rem' : '1rem'
          }}>{t('dns.results.queryInfo')}</strong>
          <div style={{ 
            marginTop: '0.5rem', 
            fontSize: isMobile ? '0.8rem' : '0.9rem', 
            color: colors.textSecondary,
            lineHeight: '1.4',
            wordBreak: 'break-all'
          }}>
            {t('dns.results.domain')}: <code style={{ 
              background: colors.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              color: colors.text,
              padding: '0.2rem 0.4rem',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>{dnsData.question[0]?.name}</code> | 
            {t('dns.results.type')}: <code style={{ 
              background: colors.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              color: colors.text,
              padding: '0.2rem 0.4rem',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>{DNS_RECORD_TYPE_NAMES[dnsData.question[0]?.type] || dnsData.question[0]?.type}</code>
          </div>
        </div>
      )}

      {dnsData.answers && renderRecordTable(dnsData.answers, t('dns.results.sections.answers'), '✅')}
      {dnsData.authority && renderRecordTable(dnsData.authority, t('dns.results.sections.authority'), '🏛️')}
      {dnsData.additional && renderRecordTable(dnsData.additional, t('dns.results.sections.additional'), '➕')}

      <div style={{
        marginTop: isMobile ? '1.5rem' : '2rem',
        padding: isMobile ? '0.8rem' : '1rem',
        background: colors.isDarkMode 
          ? 'rgba(102, 126, 234, 0.1)' 
          : 'rgba(102, 126, 234, 0.05)',
        border: `1px solid ${colors.isDarkMode ? 'rgba(102, 126, 234, 0.2)' : colors.border}`,
        borderRadius: '8px',
        fontSize: isMobile ? '0.75rem' : '0.85rem',
        color: colors.textSecondary,
        lineHeight: '1.4'
      }}>
        {t('dns.results.hint')}
      </div>

      {/* 展示原始DNS响应数据 */}
      {rawDnsData && (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>📄 {t('dns.results.rawData')}</h3>
          <button
            type="button"
            onClick={() => setShowRaw(!showRaw)}
            style={{
              padding: '0.4rem 0.8rem',
              marginBottom: '0.75rem',
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            {showRaw ? t('dns.results.rawDataHide') : t('dns.results.rawDataShow')}
          </button>
          {showRaw && (
          <div style={{
            background: colors.isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '1rem',
            fontFamily: 'Monaco, Consolas, monospace',
            fontSize: '0.85rem',
            lineHeight: '1.4',
            maxHeight: '400px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            color: colors.text
          }}>
            {JSON.stringify(rawDnsData, null, 2)}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecordTable;
